"use client";

import { useState, useRef, useCallback, useEffect } from "react";

export interface ChatRef {
  slug: string;
  title: string;
}

export interface CostInfo {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cost_usd: string;
  cost_krw: string;
  model: string;
}

interface StreamState {
  conversationId: number | null;
  messageId: number | null;
  refs: ChatRef[];
  content: string;
  isStreaming: boolean;
  status: { step: string; message: string } | null;
  error: string | null;
  cost: CostInfo | null;
  ragContext: string | null;
  parsedQuery: Record<string, string[]> | null;
  followUps: string[];
}

const API_BASE = "/api/v1";

export function useChatStream() {
  const [state, setState] = useState<StreamState>({
    conversationId: null,
    messageId: null,
    refs: [],
    content: "",
    isStreaming: false,
    status: null,
    error: null,
    cost: null,
    ragContext: null,
    parsedQuery: null,
    followUps: [],
  });

  const abortRef = useRef<AbortController | null>(null);

  // 타이핑 버퍼 — 토큰을 모아두고 글자 단위로 부드럽게 방출
  const bufferRef = useRef("");
  const displayedRef = useRef("");
  const rafRef = useRef<number | null>(null);
  const lastCharTime = useRef(0);

  const CHAR_INTERVAL = 12; // 글자당 12ms (초당 ~83자)

  const flushBuffer = useCallback(function flush() {
    const now = performance.now();
    if (bufferRef.current.length > 0 && now - lastCharTime.current >= CHAR_INTERVAL) {
      // 한 번에 1~3글자씩 방출 (버퍼가 밀리면 속도 올림)
      const charsToFlush = Math.min(Math.max(1, Math.floor(bufferRef.current.length / 10)), 3);
      const chars = bufferRef.current.slice(0, charsToFlush);
      bufferRef.current = bufferRef.current.slice(charsToFlush);
      displayedRef.current += chars;
      lastCharTime.current = now;
      setState((prev) => ({ ...prev, content: displayedRef.current }));
    }
    if (bufferRef.current.length > 0) {
      rafRef.current = requestAnimationFrame(flush);
    } else {
      rafRef.current = null;
    }
  }, []);

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState((prev) => ({ ...prev, isStreaming: false }));
  }, []);

  const send = useCallback(
    async (message: string, conversationId?: number | null, model?: string, speed?: number) => {
      // 이전 스트림 중단
      abortRef.current?.abort();

      const controller = new AbortController();
      abortRef.current = controller;
      bufferRef.current = "";
      displayedRef.current = "";
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      setState((prev) => ({
        ...prev,
        content: "",
        refs: [],
        messageId: null,
        status: { step: "connect", message: "연결 중" },
        error: null,
        cost: null,
        ragContext: null,
        parsedQuery: null,
        followUps: [],
        isStreaming: true,
      }));

      try {
        const res = await fetch(`${API_BASE}/chat`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message,
            ...(conversationId ? { conversation_id: conversationId } : {}),
            ...(model ? { model } : {}),
            ...(speed !== undefined ? { speed } : {}),
          }),
          signal: controller.signal,
        });

        if (res.status === 401) {
          setState((prev) => ({
            ...prev,
            isStreaming: false,
            error: "로그인이 필요합니다.",
          }));
          return;
        }

        if (res.status === 429) {
          const data = await res.json().catch(() => null);
          setState((prev) => ({
            ...prev,
            isStreaming: false,
            error: data?.error || "사용 한도를 초과했습니다.",
          }));
          return;
        }

        if (!res.ok) {
          setState((prev) => ({
            ...prev,
            isStreaming: false,
            error: `오류가 발생했습니다. (${res.status})`,
          }));
          return;
        }

        const reader = res.body?.getReader();
        if (!reader) {
          setState((prev) => ({
            ...prev,
            isStreaming: false,
            error: "스트림을 열 수 없습니다.",
          }));
          return;
        }

        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          // 마지막 불완전 라인은 버퍼에 유지
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const raw = line.slice(6).trim();
            if (!raw) continue;

            try {
              const evt = JSON.parse(raw);

              switch (evt.type) {
                case "status":
                  setState((prev) => ({
                    ...prev,
                    status: { step: evt.step || "thinking", message: evt.message || "처리 중" },
                  }));
                  break;

                case "context":
                  setState((prev) => ({
                    ...prev,
                    refs: (evt.refs || []) as ChatRef[],
                    ragContext: evt.rag_context || null,
                    parsedQuery: evt.parsed_query || null,
                  }));
                  break;

                case "token":
                  // status 제거 (첫 토큰 도착 시)
                  setState((prev) => (prev.status ? { ...prev, status: null } : prev));
                  // 버퍼에 추가 → rAF로 부드럽게 방출
                  bufferRef.current += evt.content || "";
                  if (!rafRef.current) {
                    rafRef.current = requestAnimationFrame(flushBuffer);
                  }
                  break;

                case "done":
                  // 남은 버퍼 즉시 방출
                  if (bufferRef.current) {
                    displayedRef.current += bufferRef.current;
                    bufferRef.current = "";
                  }
                  if (rafRef.current) {
                    cancelAnimationFrame(rafRef.current);
                    rafRef.current = null;
                  }
                  setState((prev) => ({
                    ...prev,
                    content: displayedRef.current,
                    conversationId: evt.conversation_id || prev.conversationId,
                    messageId: evt.message_id || prev.messageId,
                    isStreaming: false,
                    cost: evt.cost || null,
                    followUps: evt.follow_ups || [],
                  }));
                  break;

                case "error":
                  setState((prev) => ({
                    ...prev,
                    isStreaming: false,
                    error: evt.message || "알 수 없는 오류",
                  }));
                  break;
              }
            } catch {
              // JSON 파싱 실패 — 무시
            }
          }
        }

        // 스트림 종료 후 아직 streaming이면 완료 처리
        setState((prev) => (prev.isStreaming ? { ...prev, isStreaming: false } : prev));
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === "AbortError") {
          // 사용자 취소 — 에러 아님
          return;
        }
        setState((prev) => ({
          ...prev,
          isStreaming: false,
          error: "네트워크 오류가 발생했습니다.",
        }));
      }
    },
    [flushBuffer],
  );

  return { ...state, send, stop };
}
